import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import Cookies from "js-cookie";
import {
  ACCESS_CODE_SEARCH_PARAM,
  AUTHLEVEL_SEARCH_PARAM,
  AUTHLEVEL_SEARCH_PARAM_CAMEL_CASE,
  ERROR_CODE_SEARCH_PARAM,
  MSW_ACCESS_CODE,
  REDIRECT_SEARCH_PARAM,
} from "@/consts";
import { NX_SIGNUP_COOKIE, NX_TPA_COOKIE } from "@/consts/cookies";
import { isMobile } from "react-device-detect";
import { sentryCaptureException } from "./sentry";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getSearchParams(name: string) {
  const url = new URL(window.location.href);
  const params = url.searchParams.get(name);
  return params;
}

export function toNumber(value: unknown): number {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
}

export function getAccessCode() {
  const accessCode = getSearchParams(ACCESS_CODE_SEARCH_PARAM);
  return toNumber(accessCode);
}

export function getNexonUrl() {
  return isMobile ? import.meta.env.VITE_MOBILE_NEXON_COM_URL : import.meta.env.VITE_NEXON_COM_URL;
}

export function getRedirectUrl() {
  const redirectUrl = getSearchParams(REDIRECT_SEARCH_PARAM);

  try {
    if (redirectUrl) {
      return decodeURIComponent(redirectUrl);
    }

    return getNexonUrl();
  } catch {
    return getNexonUrl();
  }
}

export function getFallbackUrl() {
  // 페이지 진입 시 설정되어 있던 errorCode를 삭제
  const fallbackUrl = new URL(window.location.href);
  fallbackUrl.searchParams.delete(ERROR_CODE_SEARCH_PARAM);

  return fallbackUrl.toString();
}

export function getAuthLevel() {
  let authlevel = undefined;
  const authlevelSearchParam = getSearchParams(AUTHLEVEL_SEARCH_PARAM) ?? getSearchParams(AUTHLEVEL_SEARCH_PARAM_CAMEL_CASE);

  if (isMobile) {
    authlevel = 1; // 모바일은 ATL 1.0으로 처리
  } else {
    authlevel = 2; // PC는 ATL 2.0으로 처리

    if (authlevelSearchParam === "1") {
      authlevel = 1; // ATL 1.0으로 처리
    }
  }

  return authlevel;
}

// accesscode 가 MSW 값이면 PC방 판정을 비PC방으로 강제
export function getIsForcedNonCafe() {
  return getAccessCode() === MSW_ACCESS_CODE;
}

export function getCookies(name: string) {
  const cookie = Cookies.get(name);
  if (cookie) {
    return cookie;
  }
  return null;
}

export function setCookie(name: string, value: string, expires?: number) {
  Cookies.set(name, value, {
    expires,
  });
}

export function setSessionStorage(name: string, value: string) {
  if (typeof window !== "undefined" && typeof sessionStorage) {
    sessionStorage.setItem(name, value);
  }
}

export function isNumberPressed(e: React.SyntheticEvent) {
  const nativeEvent = e.nativeEvent as InputEvent;
  const key = nativeEvent?.data;
  const isNumber = key && /^[0-9]$/.test(key);

  return isNumber;
}

export function handleEnterKey(e: React.SyntheticEvent, callback: VoidFunction) {
  const nativeEvent = e.nativeEvent as KeyboardEvent;
  const key = nativeEvent?.key;

  if (key === "Enter") {
    e.preventDefault();
    callback();
  }
}

export function setSearchParamsToCookies(errorCode?: string, usetpa?: string | number, signup?: number) {
  let updatedUsetpa = usetpa ?? "-";
  let updatedSignup = signup ?? -1;

  if (errorCode) {
    if (usetpa === "-") {
      const value = Number(getCookies(NX_TPA_COOKIE) ?? 1);
      updatedUsetpa = Number.isNaN(value) ? "-" : value;
    }

    if (signup === -1) {
      const value = toNumber(getCookies(NX_SIGNUP_COOKIE));
      updatedSignup = value;
    }
  }

  return { usetpa: updatedUsetpa, signup: updatedSignup };
}

export function removeXSSUrl(url: string) {
  let result = url;
  result = result.replace(/document\.cookie/gi, ""); // document.cookie 접근 제거
  result = result.replace(/document\.write/gi, ""); // document.write 제거
  result = result.replace(/javascript:/gi, ""); // javascript: 제거
  result = result.replace(/data:/gi, ""); // data: 제거
  result = result.replace(/vbscript:/gi, ""); // vbscript: 제거
  result = result.replace(/<script.*?>.*?<\/script>/gi, ""); // script 태그 제거
  result = result.replace(/<.*?>/g, ""); // HTML 태그 제거
  result = result.replace(/alert\(/gi, ""); // alert() 제거
  result = result.replace(/on\w+\s*=\s*(['"]).*?\1/gi, ""); // 모든 onXXX=... 이벤트 핸들러 속성 제거
  result = result.replace(/eval\(/gi, ""); // eval() 제거
  result = result.replace(/'/g, ""); // 작은 따옴표 제거

  return result;
}

export function GetSafeRedirectUrl() {
  let safeUrl = getNexonUrl();

  const redirectUrl = getRedirectUrl().trim();
  if (redirectUrl && redirectUrl.length !== 0) {
    try {
      const xssSafeUrl = removeXSSUrl(redirectUrl);
      const url = new URL(xssSafeUrl);
      const host = url.host;

      // 허용된 호스트 목록
      const allowedHosts = ["nexon.com"];

      // 호스트가 허용된 목록에 있는지 확인
      if (allowedHosts.some((allowedHost) => host.endsWith(allowedHost))) {
        safeUrl = xssSafeUrl;
      }
    } catch (error) {
      sentryCaptureException(error, {
        tags: { function: "GetSafeRedirectUrl" },
        context: {
          redirectUrl: getRedirectUrl(),
        },
      });
    }
  }
  return safeUrl;
}
